/**
 * Shared formatting utilities for machine state display.
 */

export type TimezonePreference = "local" | "utc";

/**
 * "16:30" on the same calendar day as `now`, "Sep 4, 09:00" otherwise. With the
 * UTC preference the day and clock are UTC and " UTC" is appended.
 */
export function formatShortTimestamp(
  iso: string,
  timezone: TimezonePreference = "local",
  now: number = Date.now(),
): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const timeZone = timezone === "utc" ? "UTC" : undefined;
  // en-CA renders YYYY-MM-DD, which makes the same-day comparison trivial.
  const dayOf = (value: Date) => value.toLocaleDateString("en-CA", { timeZone });
  const clock = date.toLocaleTimeString("en-US", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const label =
    dayOf(date) === dayOf(new Date(now))
      ? clock
      : `${date.toLocaleDateString("en-US", { timeZone, month: "short", day: "numeric" })}, ${clock}`;
  return timezone === "utc" ? `${label} UTC` : label;
}

export function idleReasonLabel(reason: string | null | undefined): string {
  switch (reason) {
    case "no_engine":
      return "No running processes";
    case "engine_idle":
      return "Engine running, no requests";
    case "gpu_idle":
      return "GPU at 0%";
    default:
      return "Idle";
  }
}

const ENGINE_DISPLAY_NAMES: Record<string, string> = {
  vllm: "vLLM",
  sglang: "SGLang",
  trtllm: "TRT-LLM",
  dynamo: "Dynamo",
  generic: "engine",
};

export function formatEngineLabel(engine: string | null | undefined): string {
  if (!engine) return "engine";
  return ENGINE_DISPLAY_NAMES[engine] ?? engine;
}

/** Compact elapsed-time since an ISO-8601 timestamp: "<1m", "5m", "23h 44m".
 * Returns null for a missing or future timestamp. */
function shortDurationSince(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const milliseconds = Date.now() - new Date(iso).getTime();
  if (milliseconds < 0) return null;
  const totalMinutes = Math.floor(milliseconds / 60000);
  if (totalMinutes < 1) return "<1m";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function formatIdleDuration(idleSince: string | null): string | null {
  return shortDurationSince(idleSince);
}

/** How long ago the machine's current running session began, e.g. "2h 10m" or
 * "12h". Null when unknown (Fleet didn't witness the start) or in the future.
 * Drops a trailing " 0m" so a whole-hour uptime reads "12h", not "12h 0m".
 * Caller renders it as "Started {value} ago". */
export function formatStartedAgo(startedAt: string | null | undefined): string | null {
  const elapsed = shortDurationSince(startedAt);
  if (elapsed === null) return null;
  return elapsed.endsWith(" 0m") ? elapsed.slice(0, -3) : elapsed;
}

/** Age of a timestamp as a compact duration ("3d", "5h 12m", "42m"). Unlike
 * formatIdleDuration this scales to days — a dead watchdog can be down for
 * weeks. */
export function formatAge(timestamp: string | null | undefined): string | null {
  if (!timestamp) return null;
  const milliseconds = Date.now() - new Date(timestamp).getTime();
  if (milliseconds < 0) return null;
  const totalMinutes = Math.floor(milliseconds / 60000);
  if (totalMinutes < 1) return "<1m";
  const days = Math.floor(totalMinutes / 1440);
  if (days > 0) return `${days}d`;
  const hours = Math.floor(totalMinutes / 60);
  if (hours > 0) return `${hours}h ${totalMinutes % 60}m`;
  return `${totalMinutes}m`;
}
